import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type { TypeOptions } from 'react-toastify';
import { THM_USER_ID_KEY } from '../constants';
import { gb } from '../growthbook';
import { displayToast } from '../helpers/display-toast';
import { useTrackEventMutation } from '../slices';

type UseCopyToClipboardOptions<ButtonText extends ReactNode, ButtonTextOnSuccess extends ReactNode> = {
  buttonText?: ButtonText;
  buttonTextOnSuccess?: ButtonTextOnSuccess;
  /** In milliseconds */
  buttonTextResetTime?: number;
  suppressToasts?: boolean;
  addSharerId?: boolean;
  eventProperties?: CopyToClipboardEventProperties;
};

type UseCopyToClipboardReturn<ButtonText extends ReactNode, ButtonTextOnSuccess extends ReactNode> = {
  copyToClipboard: (textToCopy: string) => Promise<void>;
  copyButtonText: ButtonText | ButtonTextOnSuccess;
};

type CopyToClipboardEventProperties = {
  componentName: string;

  [k: string]: unknown;
};

/**
 * Provides:
 * - `copyToClipboard` utility function.
 * - `copyButtonText` which changes on successful copy, controlled via:
 *   - `buttonText`          // defaults to 'Copy'
 *   - `buttonTextOnSuccess` // defaults to 'Copied!'
 *   - `buttonTextResetTime`
 *
 * Automatically tracks `copy-to-clipboard` event.
 * Also provides toasts, which can be suppressed with `suppressToasts`.
 */
export const useCopyToClipboard = <
  ButtonText extends ReactNode = string,
  ButtonTextOnSuccess extends ReactNode = string,
>(
  hookOptions?: UseCopyToClipboardOptions<ButtonText, ButtonTextOnSuccess>,
): UseCopyToClipboardReturn<ButtonText, ButtonTextOnSuccess> => {
  const {
    buttonText: buttonTextNullable,
    buttonTextOnSuccess: buttonTextOnSuccessNullable,
    buttonTextResetTime = 2000,
    suppressToasts = false,
    addSharerId = false,
    eventProperties = {},
  } = hookOptions ?? {};

  /* Default values */
  const buttonText = (buttonTextNullable ?? 'Copy') as ButtonText;
  const buttonTextOnSuccess = (buttonTextOnSuccessNullable ?? 'Copied!') as ButtonTextOnSuccess;

  const [trackEvent] = useTrackEventMutation();
  const [copyButtonText, setCopyButtonText] = useState<ButtonText | ButtonTextOnSuccess>(buttonText);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const toast = useCallback(
    (type: TypeOptions, message: string) => {
      suppressToasts || displayToast(type, message);
    },
    [suppressToasts],
  );

  const copyToClipboard = useCallback(
    async (textToCopy: string) => {
      if (!navigator.clipboard) {
        toast('error', 'Looks like clipboard is not supported in your browser.');
        return;
      }

      try {
        const finalText = addSharerId ? addSharerIdToUrl(textToCopy) : textToCopy;
        await navigator.clipboard.writeText(finalText);
        toast('success', 'Copied to clipboard!');

        clearTimeout(timeoutRef.current);
        setCopyButtonText(buttonTextOnSuccess ?? buttonText);

        timeoutRef.current = setTimeout(() => {
          setCopyButtonText(buttonText);
        }, buttonTextResetTime);
      } catch (error) {
        toast('error', 'Oops! Could not copy to clipboard.');
      }

      try {
        void trackEvent({
          event: 'copy-to-clipboard',
          properties: {
            ...eventProperties,
            value: textToCopy,
          },
        });
      } catch {}
    },
    [buttonText, buttonTextOnSuccess, buttonTextResetTime, toast, addSharerId, trackEvent, eventProperties],
  );

  return { copyToClipboard, copyButtonText };
};

const addSharerIdToUrl = (url: string): string => {
  const isSharerIdEnabled = gb.isOn('tracking-sharer-id');
  if (!isSharerIdEnabled) return url;

  const sharerId = localStorage.getItem(THM_USER_ID_KEY);
  if (!sharerId) return url;

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}sharerId=${sharerId}`;
};
